import * as THREE from 'three';
import { BaseScene, SceneMetadata } from './base-scene';
import { AdvancedClipping, ClippingConfig } from '../clipping-module';

/**
 * Configuration for clipping scene
 */
export interface ClippingSceneConfig extends ClippingConfig {
  position?: THREE.Vector3;
  scale?: number;
  showLabel?: boolean;
  labelText?: string;
  labelPosition?: THREE.Vector3;
  labelColor?: string;
}

/**
 * Scene that demonstrates advanced clipping techniques
 */
export class ClippingScene extends BaseScene {
  private clipping: AdvancedClipping | null = null;
  private config: ClippingSceneConfig;
  private labelMesh: THREE.Mesh | null = null;

  constructor(config: ClippingSceneConfig = {}) {
    super({
      id: 'clipping-demo',
      name: 'Advanced Clipping Demo',
      description: 'Demonstrates advanced clipping planes with animated instanced geometry'
    });
    this.config = config;
  }

  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void {
    if (this.initialized) {
      console.warn('ClippingScene already initialized');
      return;
    }

    // Add root to parent
    parent.add(this.root);

    // Add lighting
    this.addAmbientLight(0xffffff, 0.5);

    // Add directional lights for shadows
    const dirLight1 = this.addDirectionalLight(0xffffff, 0.8, new THREE.Vector3(5, 10, 7.5));
    dirLight1.castShadow = true;
    dirLight1.shadow.mapSize.width = 2048;
    dirLight1.shadow.mapSize.height = 2048;
    dirLight1.shadow.camera.near = 1;
    dirLight1.shadow.camera.far = 20;
    dirLight1.shadow.camera.left = -5;
    dirLight1.shadow.camera.right = 5;
    dirLight1.shadow.camera.top = 5;
    dirLight1.shadow.camera.bottom = -5;

    // Add fill light from opposite side
    const dirLight2 = this.addDirectionalLight(0x4444ff, 0.3, new THREE.Vector3(-3, 5, -5));

    // Optional: Add hemisphere light for more natural lighting
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
    this.root.add(hemiLight);

    // Create clipping module
    this.clipping = new AdvancedClipping(renderer, {
      enabled: this.config.enabled ?? false,
      shadows: this.config.shadows ?? false,
      visualize: this.config.visualize ?? false,
      color: this.config.color ?? 0x00ff00,
      shininess: this.config.shininess ?? 100,
      globalClipping: this.config.globalClipping ?? false
    });

    // Configure position and scale
    const clippingObject = this.clipping.getObject();

    if (this.config.scale !== undefined) {
      clippingObject.scale.setScalar(this.config.scale);
    }

    if (this.config.position) {
      this.clipping.setPosition(
        this.config.position.x,
        this.config.position.y,
        this.config.position.z
      );
    }

    // Add to scene
    this.clipping.addToScene(this.root);

    // Add text label if requested
    if (this.config.showLabel) {
      this.addTextLabel();
    }

    this.initialized = true;
    console.log(`✅ ClippingScene initialized`);
  }

  /**
   * Add a text label using canvas texture
   */
  private addTextLabel(): void {
    const text = this.config.labelText || 'Clipping Demo';
    const color = this.config.labelColor || '#ffffff';

    // Create canvas for text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 512;
    canvas.height = 128;

    // Draw text
    context.fillStyle = 'rgba(0, 0, 0, 0.6)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.font = 'bold 48px Arial';
    context.fillStyle = color;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create material and mesh
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide
    });

    const geometry = new THREE.PlaneGeometry(1, 0.25);
    this.labelMesh = new THREE.Mesh(geometry, material);

    // Position the label
    const labelPos = this.config.labelPosition || new THREE.Vector3(0, 0.8, 0);
    this.labelMesh.position.copy(labelPos);

    // Make it face the camera (billboard effect)
    this.labelMesh.rotation.x = -Math.PI / 2;

    this.root.add(this.labelMesh);
    console.log(`📝 Text label added: "${text}"`);
  }

  update(delta: number): void {
    if (!this.initialized || !this.clipping) return;
    this.clipping.update();
  }

  dispose(): void {
    // Clipping module doesn't have explicit dispose, but we can clean up
    this.clipping = null;
    super.dispose();
  }

  /**
   * Get the clipping module for direct access
   */
  getClippingModule(): AdvancedClipping | null {
    return this.clipping;
  }

  /**
   * Update clipping visualization visibility
   */
  setVisualizationVisible(visible: boolean): void {
    if (this.clipping) {
      this.clipping.setVisualizationVisible(visible);
    }
  }

  /**
   * Update clipping shadows
   */
  setClipShadows(enabled: boolean): void {
    if (this.clipping) {
      this.clipping.setClipShadows(enabled);
    }
  }
}
